/**
 * "Return to this page after sign-in" targets (`?next=`), shared by the
 * middleware, the sign-in form and the SSO callback. Framework-free so the
 * middleware runtime can import it.
 *
 * Only a same-origin path survives: `/de/leads/12?tab=calls`. Absolute URLs,
 * protocol-relative `//host`, backslashes, control characters, API paths and
 * the sign-in page itself are dropped — an open redirect can never ride along.
 */

const LOCALE_PREFIX = /^\/(en|de)(\/|\?|#|$)/;

export function sanitizeNextPath(raw: string | null | undefined): string | null {
  if (typeof raw !== "string") return null;
  let value = raw.trim();
  if (!value || value.length > 2048) return null;
  try {
    value = decodeURIComponent(value);
  } catch {
    /* keep as is */
  }
  if (!value.startsWith("/") || value.startsWith("//") || value.startsWith("/\\")) return null;
  if (/[\\\r\n\t\0]/.test(value) || value.startsWith("/api/")) return null;
  if (/^\/(en|de)?\/?(sign-in|api)(\/|\?|$)/.test(value)) return null;
  return value;
}

/** `/leads/12` → `/de/leads/12`; a path that already carries a locale is kept. */
export function withLocale(path: string, locale: string): string {
  return LOCALE_PREFIX.test(path) ? path : `/${locale}${path.startsWith("/") ? "" : "/"}${path}`;
}

/** The sign-in URL for a locale, carrying the page the visitor asked for. */
export function signInUrlFor(locale: string, next: string | null): string {
  const safe = sanitizeNextPath(next);
  return safe ? `/${locale}/sign-in?next=${encodeURIComponent(safe)}` : `/${locale}/sign-in`;
}
